interface Article {
  tag: string;
  title: string;
  excerpt: string;
  date: string;
  reads: string;
}

interface Props {
  id: string;
  en: string;
  title: string;
  lead: string;
  icon: string;
  accent: "primary" | "accent" | "secondary";
  reverse?: boolean;
  articles: Article[];
  heroImage: string;
}

const tone: Record<string, { label: string; bar: string; chip: string; icon: string; soft: string }> = {
  primary: { label: "text-primary-600", bar: "bg-primary-500", chip: "bg-primary-100 text-primary-800", icon: "bg-primary-100 text-primary-700", soft: "hover:bg-background-100" },
  accent: { label: "text-accent-700", bar: "bg-accent-500", chip: "bg-accent-100 text-accent-900", icon: "bg-accent-100 text-accent-800", soft: "hover:bg-background-100" },
  secondary: { label: "text-secondary-700", bar: "bg-secondary-500", chip: "bg-secondary-100 text-secondary-900", icon: "bg-secondary-100 text-secondary-800", soft: "hover:bg-background-100" },
};

export default function ArticleSection({ id, en, title, lead, icon, accent, reverse, articles, heroImage }: Props) {
  const t = tone[accent];
  const [leadArticle, ...rest] = articles;

  return (
    <section id={id} className={`scroll-mt-24 w-full ${reverse ? "bg-background-100/50" : "bg-background-50"}`}>
      <div className="mx-auto grid w-full max-w-[1200px] gap-8 px-4 md:px-6 py-12 md:py-16 lg:grid-cols-12">
        {/* intro + hero image */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 text-xs font-medium tracking-widest text-foreground-400">
            <span className={`h-px w-8 ${t.bar}`}></span>
            {en}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${t.icon}`}>
              <i className={`${icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
            </span>
            <h2 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{title}</h2>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{lead}</p>

          <div className="relative mt-6 hidden overflow-hidden rounded-lg lg:block">
            <img src={heroImage} alt={`${title}栏目配图`} className="h-56 w-full object-cover" width={900} height={500} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </div>
        </div>

        {/* lead article + list */}
        <div className="lg:col-span-7">
          <article
            className={`group rounded-lg bg-background-100/70 border border-background-200/70 p-5 transition-colors ${t.soft}`}
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={`whitespace-nowrap rounded px-2.5 py-1 text-xs font-medium ${t.chip}`}>推荐</span>
              <span className="text-xs text-foreground-400">{leadArticle.date}</span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {leadArticle.reads}
              </span>
            </div>
            <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
              {leadArticle.title}
            </h3>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{leadArticle.excerpt}</p>
            <a href={`#${id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
              阅读全文
              <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
            </a>
          </article>

          <ul className="mt-3 divide-y divide-background-200/70 rounded-lg bg-background-50/60 border border-background-200/60 px-1">
            {rest.map((a) => (
              <li key={a.title}>
                <a href={`#${id}`} className="group flex items-start gap-3 rounded-lg px-3 py-4 transition-colors hover:bg-background-100">
                  <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${t.bar}`}></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`whitespace-nowrap text-xs font-medium ${t.label}`}>{a.tag}</span>
                      <span className="text-xs text-foreground-400">{a.date}</span>
                    </div>
                    <h4 className="mt-1 text-base font-medium leading-snug text-foreground-900 group-hover:text-primary-700 transition-colors">
                      {a.title}
                    </h4>
                  </div>
                  <span className="mt-1 hidden shrink-0 items-center gap-1 text-xs text-foreground-400 sm:flex">
                    <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {a.reads}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}